import * as React from "react";
import { FC } from "react";
import Email from "@assets/svg/icons/email.svg";
import { HeapPHIDecorator } from "@app/components/analytics";
import { maskEmail } from "@app/utils";
import { Button } from "@natera/material/lib/button";
import { Link } from "react-router-dom";
import { SpinnerView } from "@app/components";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import { Form, LinkButton } from "@natera/form";

import "./sentEmailAfterUnlockView.scss";

const messages = defineMessages({
  sentEmailAfterUnlockViewCheckMail: {
    id: "sentEmailAfterUnlockViewCheckMail",
    defaultMessage: "Check your email",
  },
  sentEmailAfterUnlockViewInstructions: {
    id: "sentEmailAfterUnlockViewInstructions",
    defaultMessage: "We have sent instructions to your email.",
  },
  sentEmailAfterUnlockViewValidLink: {
    id: "sentEmailAfterUnlockViewValidLink",
    defaultMessage: "The link is valid only for {time} minutes.",
  },
  sentEmailAfterUnlockViewResendResetLinkButton: {
    id: "sentEmailAfterUnlockViewResendResetLinkButton",
    defaultMessage: "Resend Link",
  },
  sentEmailAfterUnlockViewBack: {
    id: "sentEmailAfterUnlockViewBack",
    defaultMessage: "Back",
  },
  sentEmailAfterUnlockViewDone: {
    id: "sentEmailAfterUnlockViewDone",
    defaultMessage: "Done",
  },
  sentEmailAfterUnlockViewCheckSpam: {
    id: "sentEmailAfterUnlockViewCheckSpam",
    defaultMessage:
      "Didn't receive the email? Check your spam filter,{br} or {link}",
  },
  sentEmailAfterUnlockViewTryAnotherEmail: {
    id: "sentEmailAfterUnlockViewTryAnotherEmail",
    defaultMessage: "Try another email address",
  },
});

interface SentEmailAfterUnlockViewProps {
  email: string;
  handleResendEmail?: () => void;
  resendIsDisabled?: boolean;
  isLoading: boolean;
  tryAnotherEmailLink?: string;
  handleTryAnotherEmail?: () => void;
  /**
   * When set, the primary CTA renders as a "Back" link button pointing at
   * `backLink` instead of the default "Resend Link" submit button. Used by
   * the unlock flow per UPP-5517 IND-1.
   */
  backLink?: string;
  /**
   * Side-effect handler invoked when the user clicks the "Back" link button.
   * Necessary when `backLink` points to the same route the view is rendered
   * on (e.g. /unlock), so the parent can reset its local state and the
   * underlying form view can re-render.
   */
  handleBack?: () => void;
  /**
   * When provided, replaces the default "Did not receive the email?" footer
   * entirely. Used by the unlock flow per UPP-5517 IND-1, which renders a
   * longer informational message with no "Try another email" link.
   */
  footerOverride?: React.ReactNode;
}

const SentEmailAfterUnlockView: FC<SentEmailAfterUnlockViewProps> = ({
  email,
  handleResendEmail,
  resendIsDisabled,
  isLoading,
  tryAnotherEmailLink,
  handleTryAnotherEmail,
  backLink,
  handleBack,
  footerOverride,
}) => {
  const intl = useIntl();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleResendEmail?.();
  };

  const primaryCta = backLink ? (
    <LinkButton
      renderLink={(props) => <Link {...props} onClick={handleBack} />}
      to={backLink}
      outlined
    >
      {intl.formatMessage(messages.sentEmailAfterUnlockViewBack)}
    </LinkButton>
  ) : (
    <Button type="submit" outlined disabled={resendIsDisabled}>
      {intl.formatMessage(
        messages.sentEmailAfterUnlockViewResendResetLinkButton
      )}
    </Button>
  );

  const defaultFooter = tryAnotherEmailLink ? (
    <p>
      {intl.formatMessage(messages.sentEmailAfterUnlockViewCheckSpam, {
        br: <br />,
        link: (
          <Link to={tryAnotherEmailLink} onClick={handleTryAnotherEmail}>
            {intl.formatMessage(
              messages.sentEmailAfterUnlockViewTryAnotherEmail
            )}
          </Link>
        ),
      })}
    </p>
  ) : null;

  return (
    <div className="send-email-after-unlock">
      <div className="image-container">
        <Svg content={Email} />
      </div>
      <div className="description-container">
        <h2>
          {intl.formatMessage(messages.sentEmailAfterUnlockViewCheckMail)}
        </h2>

        <p>
          {intl.formatMessage(messages.sentEmailAfterUnlockViewInstructions)}
        </p>

        <p>
          <HeapPHIDecorator protectText noWrap>
            <span>{maskEmail(email || "")}</span>
          </HeapPHIDecorator>
        </p>

        <p>
          {intl.formatMessage(messages.sentEmailAfterUnlockViewValidLink, {
            time: "60",
          })}
        </p>
      </div>
      <Form
        onSubmit={handleSubmit}
        className="send-email-after-unlock-form"
        buttons={
          <>
            {primaryCta}

            <LinkButton
              renderLink={(props) => <Link {...props} />}
              to={"/"}
              raised
            >
              {intl.formatMessage(messages.sentEmailAfterUnlockViewDone)}
            </LinkButton>
          </>
        }
        noValidate
      ></Form>

      {footerOverride ?? defaultFooter}

      <SpinnerView isLoading={isLoading} />
    </div>
  );
};

export default SentEmailAfterUnlockView;
